import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { authGet } from "../../lib";

/**
 * Account search — a port of the legacy account_search.php.
 *
 * Deliberately search-first: there are 31,000 customers, and the legacy screen
 * likewise listed nothing until something was typed.
 */

const useStyles = makeStyles((theme) => ({
  page: { maxWidth: 1360, margin: "24px auto 0", width: "100%" },
  panel: { padding: theme.spacing(2.5), marginBottom: theme.spacing(2) },
  head: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  title: { fontSize: 18, fontWeight: 500, flex: "none" },
  spacer: { flex: 1 },
  count: { color: theme.palette.text.secondary, fontSize: 13 },
  form: {
    display: "grid",
    gridTemplateColumns: "repeat(12, 1fr)",
    gap: theme.spacing(1.5),
    alignItems: "center",
  },
  row: {
    cursor: "pointer",
    height: 36,
    "&:hover": { backgroundColor: "rgba(49,81,121,0.08)" },
  },
  cell: { fontSize: 13, padding: "4px 10px" },
  headCell: { fontSize: 12, fontWeight: 600, padding: "6px 10px" },
  strong: { color: "rgba(0,0,0,0.87)" },
  muted: { color: theme.palette.text.secondary },
  truncate: { maxWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  empty: { padding: theme.spacing(4), textAlign: "center" },
  error: { color: "#c62828", fontSize: 13 },
}));

export function AccountsPage() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const [form, setForm] = useState({
    billname: params.get("billname") ?? "",
    phone: params.get("phone") ?? "",
    email: params.get("email") ?? "",
  });
  const [rows, setRows] = useState([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const search = location.search;

  const load = useCallback(async () => {
    const q = new URLSearchParams(search);
    const has = ["billname", "phone", "email"].some((k) => (q.get(k) ?? "").trim());
    if (!has) {
      setRows([]);
      setSearched(false);
      return;
    }

    setLoading(true);
    setError("");
    const { data, error: err } = await authGet(`/accounts?${q.toString()}`);
    setLoading(false);

    if (err) {
      setError(err.message || "Could not search accounts.");
      return;
    }
    setRows(data?.rows ?? []);
    setSearched(!!data?.searched);
  }, [search]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const q = new URLSearchParams(search);
    setForm({
      billname: q.get("billname") ?? "",
      phone: q.get("phone") ?? "",
      email: q.get("email") ?? "",
    });
  }, [search]);

  const submit = (e) => {
    e?.preventDefault?.();
    const q = new URLSearchParams();
    Object.entries(form).forEach(([k, v]) => {
      if (v.trim()) q.set(k, v.trim());
    });
    history.push(q.toString() ? `/accounts?${q.toString()}` : "/accounts");
  };

  const clear = () => {
    setForm({ billname: "", phone: "", email: "" });
    history.push("/accounts");
  };

  const set = (name) => (e) => setForm((f) => ({ ...f, [name]: e.target.value }));

  return (
    <Box className={classes.page}>
      <Paper className={classes.panel}>
        <div className={classes.head}>
          <Typography className={classes.title}>Search Accounts</Typography>
          <div className={classes.spacer} />
          {searched && (
            <span className={classes.count}>
              {loading ? "Searching…" : `${rows.length.toLocaleString()} found`}
            </span>
          )}
        </div>

        <form onSubmit={submit}>
          <div className={classes.form}>
            <TextField
              style={{ gridColumn: "span 4" }}
              label="Billing Name"
              value={form.billname}
              onChange={set("billname")}
            />
            <TextField
              style={{ gridColumn: "span 3" }}
              label="Phone"
              value={form.phone}
              onChange={set("phone")}
            />
            <TextField
              style={{ gridColumn: "span 3" }}
              label="Email"
              value={form.email}
              onChange={set("email")}
            />
            <div style={{ gridColumn: "span 2", display: "flex", gap: 8 }}>
              <Button type="submit" size="small" variant="contained" color="primary">
                Search
              </Button>
              {searched && (
                <Button size="small" onClick={clear}>
                  Clear
                </Button>
              )}
            </div>
          </div>
        </form>

        {error && <div className={classes.error} style={{ marginTop: 12 }}>{error}</div>}
      </Paper>

      {loading && (
        <div className={classes.empty}>
          <CircularProgress size={22} />
        </div>
      )}

      {!loading && searched && (
        <Paper className={classes.panel}>
          {!rows.length ? (
            <div className={classes.empty}>
              <span className={classes.muted}>No accounts match that search.</span>
            </div>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.headCell} style={{ width: 90 }}>Account</TableCell>
                  <TableCell className={classes.headCell}>Name</TableCell>
                  <TableCell className={classes.headCell}>Email</TableCell>
                  <TableCell className={classes.headCell} style={{ width: 130 }}>Phone</TableCell>
                  <TableCell className={classes.headCell} style={{ width: 180 }}>Location</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow
                    key={r.account_id}
                    className={classes.row}
                    onClick={() => history.push(`/accounts/${r.account_id}`)}
                  >
                    <TableCell className={`${classes.cell} ${classes.strong}`}>
                      {r.account_id}
                    </TableCell>
                    <TableCell className={`${classes.cell} ${classes.strong}`}>
                      {[r.bill_first_name, r.bill_last_name].filter(Boolean).join(" ") || (
                        <span className={classes.muted}>—</span>
                      )}
                    </TableCell>
                    <TableCell className={`${classes.cell} ${classes.truncate}`} title={r.email}>
                      {r.email}
                    </TableCell>
                    <TableCell className={classes.cell}>
                      {r.home_phone || <span className={classes.muted}>—</span>}
                    </TableCell>
                    <TableCell className={classes.cell}>
                      {[r.city, r.state].filter(Boolean).join(", ") || (
                        <span className={classes.muted}>—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}

      {!loading && !searched && (
        <div className={classes.empty}>
          <span className={classes.muted}>
            Enter a billing name, phone number or email to search.
          </span>
        </div>
      )}
    </Box>
  );
}

export default AccountsPage;
